import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft, AlertTriangle, Receipt, ClipboardList, User, Sparkles, ExternalLink, ListOrdered, HardHat, Mail, Printer, Home,
} from "lucide-react";
import { EmailTemplateModal } from "@/features/comms/EmailTemplateModal";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CustomerAvatar } from "@/features/customer-360/CustomerAvatar";
import { CustomerProseSummary } from "@/features/customer-360/CustomerProseSummary";
import { CustomerLink, TechLink } from "@/components/links";
import { useCustomer } from "@/features/customer-360/api";
import { useOwnerEra } from "@/features/customer-360/ownerEra";
import { displayCustomerName } from "@/lib/format";
import { useInvoice, useInvoiceLines, useInvoiceTechs } from "./api";
import { useBackTarget, backText } from "@/lib/navBack";
import { CopyCode } from "@/components/Copyable";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";

// ============================================================================
// InvoiceDetailView — /invoice/:invoice  (NAV-01)
// Header-level invoice from mirror_sales + the dispatch <-> customer links.
// Full line-items / AR = MIR-11 (read-only slide-out, later).
// ============================================================================

function money(v: number | null): string {
  if (v === null) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function dateLabel(d: string | null): string {
  if (!d) return "—";
  try { return format(parseISO(d), "EEEE, MMMM d, yyyy"); } catch { return d; }
}

export function InvoiceDetailView() {
  const { invoice: invoiceId } = useParams<{ invoice: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data: inv, isLoading, isError, error } = useInvoice(invoiceId);
  const { data: customer } = useCustomer(inv?.custno);
  const { data: lines } = useInvoiceLines(invoiceId);
  const { data: techRows } = useInvoiceTechs(invoiceId);
  // PROP-STORY-01: who owned the home when this invoice was written. A reused
  // ESC account shows the CURRENT name everywhere — but ESC snapshotted the
  // era-correct name on the invoice row (ship_name), and the era hook knows
  // the handoff date.
  const era = useOwnerEra(inv?.custno, inv?.locno);
  // TMPL: Email Invoice — preview + send the rendered invoice email.
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    document.title = inv ? `Pegasus · Invoice ${inv.invoice}` : "Pegasus · Invoice";
  }, [inv]);

  // Prefer the recorded referrer (NAV-03); fall back to legacy ?from=, then to
  // browser-back for direct entry.
  const from = searchParams.get("from");
  const fallbackLabel = from === "dispatch" ? "dispatch" : from === "customer" ? "customer" : "back";
  const fallbackUrl: string | null = (() => {
    if (from === "dispatch") {
      const d = searchParams.get("dispatch");
      return d ? `/dispatch/${d}` : null;
    }
    if (from === "customer" && inv) return `/customers/${inv.custno}`;
    return null;
  })();
  const back = useBackTarget({ url: fallbackUrl ?? "", label: fallbackLabel });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-8">
        <Card className="h-12 animate-pulse bg-slate-100" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="h-80 animate-pulse bg-slate-100 lg:col-span-2" />
          <Card className="h-64 animate-pulse bg-slate-100" />
        </div>
      </div>
    );
  }

  if (isError || !inv) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <Card className="border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <div>
              <h3 className="text-sm font-semibold text-red-900">
                Couldn&apos;t load invoice {invoiceId}
              </h3>
              <p className="mt-1 text-xs text-red-700">
                {(error as Error)?.message ?? "Invoice not found."}
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const customerName = displayCustomerName(null, inv.customer_fullname ?? customer?.fullname ?? null);
  // PROP-STORY-01: this invoice predates the ownership handoff → it belongs to
  // the PREVIOUS owner's era. servedName = ESC's own name snapshot from the
  // invoice (exactly what printed then), falling back to the era record.
  const priorEraOwner = era.priorOwnerFor(inv.inv_date);
  const servedName = priorEraOwner ? ((inv.ship_name ?? "").trim() || priorEraOwner) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="mx-auto max-w-5xl p-8"
    >
      <button
        type="button"
        onClick={() => (back.url ? navigate(back.url) : navigate(-1))}
        className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors duration-150 ease-out-quart hover:text-brand-700"
      >
        <ArrowLeft className="h-3 w-3" />
        {backText(back.label)}
      </button>

      <section className="mb-6">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-2xl font-bold text-slate-900">
            Invoice <CopyCode value={inv.invoice} label="Copy invoice number" variant="text" size="lg" className="text-2xl font-bold text-slate-700" />
          </h2>
          <span className="text-sm text-slate-500">{dateLabel(inv.inv_date)}</span>
          {inv.inv_type && (
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
              {inv.inv_type}
            </span>
          )}
          {inv.dept && (
            <span className="rounded-full bg-slate-50 px-2.5 py-0.5 text-xs text-slate-700">
              Dept {inv.dept}
            </span>
          )}
          <button
            type="button"
            onClick={() => window.open(`/invoices/esc/${inv.invoice}`, "_blank")}
            title="Open the customer-facing invoice — print or save as PDF"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            <Printer className="h-3.5 w-3.5" />
            Print / Customer View
          </button>
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            title="Email this invoice — preview first, then send"
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            <Mail className="h-3.5 w-3.5" />
            Email Invoice
          </button>
        </div>

        {/* PROP-STORY-01: a pre-handoff invoice belongs to the previous owner —
            say so in words, with ESC's own name snapshot from the invoice. */}
        {servedName && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50/70 px-3 py-2 text-sm text-slate-600">
            <Home className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="font-semibold text-slate-800">{servedName}&apos;s invoice</span>
            <span className="rounded bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-700">previous owner</span>
            <span className="text-slate-500">
              The home changed hands{era.handoffDate ? ` around ${format(parseISO(era.handoffDate), "MMM yyyy")}` : ""} — the account now belongs to {customerName}.
            </span>
          </div>
        )}
      </section>

      {emailOpen && (
        <EmailTemplateModal
          custno={inv.custno}
          customerName={customerName}
          templateKey="invoice_email"
          templateParams={{ invoice: inv.invoice }}
          title={`Email Invoice ${inv.invoice} — ${customerName}`}
          messageType="invoice"
          onClose={() => setEmailOpen(false)}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left — amounts + meta */}
        <div className="space-y-4 lg:col-span-2">
          <Card className="p-5">
            <SectionHeader icon={Receipt} label="Invoice total" />
            <div className="text-3xl font-bold text-slate-900">{money(inv.inv_amount)}</div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <Stat label="Labor" value={money(inv.labor_cost)} />
              <Stat label="Material" value={money(inv.mat_cost)} />
              {inv.other_cost ? <Stat label="Other" value={money(inv.other_cost)} /> : null}
            </div>
            {(() => {
              // Gross profit footer — only when ESC recorded any cost at all
              // (the Jennifer Mueller rule: numbers only with evidence).
              const costs = (inv.labor_cost ?? 0) + (inv.mat_cost ?? 0) + (inv.other_cost ?? 0);
              if (costs <= 0 || inv.inv_amount === null) return null;
              return (
                <p className="mt-3 border-t border-slate-100 pt-3 text-sm">
                  <span className="text-xs text-slate-500">Gross profit </span>
                  <span className="font-semibold text-emerald-700">
                    {money(inv.inv_amount - costs)}
                  </span>
                </p>
              );
            })()}
          </Card>

          {/* MIR-11: the invoice grid — what ESC shows on its Invoice tab.
              INV-CUST-01 (Seth 2026-09-30, the R&D Batteries close): section
              headers ("Main Floor System:") read as headers, ESC's spacer
              lines collapse, and every line the customer will NEVER see
              (no_print) is labeled — so the office can tell at a glance what
              the customer copy holds vs the internal story. */}
          {(lines ?? []).length > 0 && (
            <Card className="p-5">
              <SectionHeader icon={ListOrdered} label="Line items"
                info={<>Every line on the ESC invoice, in order. Amber lines are internal — they never print on the customer copy (use Print / Customer View up top for exactly what the customer sees).</>} />
              <ul className="divide-y divide-slate-100">
                {(lines ?? []).map((l) => {
                  const desc = (l.line_desc ?? "").trim();
                  if (!desc && !l.amount && !l.quan) return null; // ESC spacer rows
                  const header = /:$/.test(desc) && !l.amount && !l.quan;
                  return (
                    <li key={l.line_count} className={cn("flex items-start gap-3 py-2 text-sm", l.no_print && "bg-amber-50/60")}>
                      <span className="w-12 shrink-0 pt-0.5 font-mono text-[11px] text-slate-400">
                        {l.prod ?? ""}
                      </span>
                      <span className={cn("min-w-0 flex-1 whitespace-pre-wrap", header ? "pt-1 font-bold text-slate-900" : "text-slate-700")}>
                        {desc || <span className="italic text-slate-400">—</span>}
                        {l.no_print && (
                          <span className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-amber-700" title="Internal — never prints on the customer copy">
                            internal
                          </span>
                        )}
                      </span>
                      <span className="w-12 shrink-0 text-right tabular-nums text-slate-500">
                        {l.quan ? l.quan : ""}
                      </span>
                      <span className="w-24 shrink-0 text-right font-medium tabular-nums text-slate-900">
                        {l.amount ? money(l.amount) : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}

          {/* MIR-11 + Seth 2026-06-11: "actual hours and techs on the
              invoice" — ESC's Technicians tab, mirrored. */}
          {(techRows ?? []).length > 0 && (
            <Card className="p-5">
              <SectionHeader icon={HardHat} label="Technicians on this invoice"
                info={<>Hours and labor cost as recorded in ESC. % credit is the tech&apos;s commission credit on this ticket.</>} />
              <ul className="divide-y divide-slate-100">
                {(techRows ?? []).map((t) => (
                  <li key={t.line_count} className="flex items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      {t.empno ? (
                        <TechLink empno={t.empno} name={t.name ?? t.empno} />
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </span>
                    <span className="shrink-0 tabular-nums text-slate-700">
                      {t.reg_hr ? `${t.reg_hr}h` : "0h"}
                      {t.ot_hr ? (
                        <span className="text-amber-700"> + {t.ot_hr}h OT</span>
                      ) : null}
                    </span>
                    <span className="w-24 shrink-0 text-right tabular-nums text-slate-500">
                      {t.lab_amt ? money(t.lab_amt) : ""}
                    </span>
                    {t.credit_pct ? (
                      <span className="shrink-0 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                        {t.credit_pct}% credit
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card className="p-5">
            <SectionHeader icon={User} label="Details" />
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              <Field label="Salesman" value={inv.salesman_name ?? inv.salesman ?? "—"} />
              <Field label="PO #" value={inv.po_num ?? "—"} />
              <Field label="Entered" value={inv.ent_date ? dateLabel(inv.ent_date) : "—"} />
            </dl>
          </Card>

          {/* Dispatch link */}
          {inv.dispatch && (
            <button
              type="button"
              onClick={() => navigate(`/dispatch/${inv.dispatch}?from=invoice`)}
              className="group flex w-full items-center justify-between rounded-xl border border-hairline bg-white p-4 text-left shadow-e1 transition-all duration-200 ease-out-quart hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 motion-reduce:hover:translate-y-0"
            >
              <span className="inline-flex items-center gap-2 text-sm text-slate-700">
                <ClipboardList className="h-4 w-4 text-slate-400" />
                From dispatch <span className="font-mono font-medium text-slate-900">#{inv.dispatch}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
                View dispatch <ExternalLink className="h-3.5 w-3.5" />
              </span>
            </button>
          )}
        </div>

        {/* Right — customer */}
        <div className="space-y-4">
          <Card className="p-5">
            <SectionHeader icon={Sparkles} label="Customer" />
            <div className="flex items-start gap-3">
              <CustomerAvatar custno={inv.custno} name={customerName} size="md" />
              <div className="min-w-0 flex-1">
                <CustomerLink custno={inv.custno} locno={inv.locno ?? undefined} className="block text-sm">
                  {customerName}
                </CustomerLink>
                <div className="mt-0.5 font-mono text-xs text-slate-500">{inv.custno}</div>
                {servedName && (
                  <div className="mt-1 inline-flex max-w-full items-center truncate rounded bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-700" title="The account carries the current owner; this invoice was written in the previous owner's era">
                    Served then: {servedName}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3">
              <CustomerProseSummary custno={inv.custno} locationCount={customer?.locations?.length ?? 1} />
            </div>
            <div className="mt-4 border-t border-slate-100 pt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/customers/${inv.custno}`)}
                className="w-full"
              >
                Qualify
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </motion.div>
  );
}

function SectionHeader({ icon: Icon, label, info }: { icon: React.ComponentType<{ className?: string }>; label: string; info?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
      <Icon className="h-3 w-3" />
      {label}
      {info && <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip>}
    </div>
  );
}
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-medium text-slate-900">{value}</div>
    </div>
  );
}
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-slate-900">{value}</dd>
    </div>
  );
}
